import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import GlobalState from '../../../../_common/platform/ts/globalStates/GlobalState';
import { EntityModelsStore } from '../../../../pdc-platform/src/ts/stores/EntityModelsStore';
import { getTimeZoneFromTheme, isTypeOfDate } from '../../ts/DateTimeFunctions';
import * as Globalization from '../../ts/Globalization';
import { IValuesCollection } from '../../ts/appTheme';
import { convertIntervalValueToAttrQueryParam } from '../IntervalsSelect';

import {
    IGlobalAttributeFilterCollection,
    IGlobalAttributeFilter,
    IGlobalAttributeFilters,
    IAttribute,
    QueryFilterValueType,
    AttributeSelectionType,
    IScopeOptions,
    IAttributeDataQuery,
    IPeriodFilter,
    IAttributeName
} from './Interfaces';

import {
    convertAttributeTypeToControlType,
    createAttributeID,
    getNormalizedDisplayValues,
    isFiscalTimePart,
    isKeySelect,
    relationDoesNotAllowToHaveValue
} from './FilterUtilities';

import { IIntervalParameters, IIntervals } from '../../ts/AqlUtility';

import { ATTRIBUTE_TYPES, TIME_PART_TYPES } from '../../ts/ConfigurationConstants';

import { CLICK_TO_FILTER_NOT_USED_VALUE } from './Constants';

export type ClickToFilterMode = 'instantApply' | 'delayedApply' | 'cumulative' | 'drillThrough';

export interface IDDAttributeObject extends IAttributeName {
    clickToFilterAttribute?: IAttribute;
    intervals?: IIntervals;
    controlType?: string;
    selectionType?: AttributeSelectionType;
}

export interface IDDAttribute {
    attributeObject: IDDAttributeObject;
    value: any[];
    options?: {
        dataQuery?: IAttributeDataQuery;
    };
}

function normalizeFilterValue(
    value: any,
    attributeType: string,
    timePart: string,
    runTimeWidgetID: number,
    glob: IGlob
) {
    if (typeof value === 'string') {
        if (attributeType === ATTRIBUTE_TYPES.REAL) {
            if (isNaN(parseFloat(value))) {
                glob.AppActions.showNotification_Fail(
                    `Fail to convert '${value}' value to real. Types of source and target Attributes must be the same.`,
                    runTimeWidgetID
                );
                return null;
            }
        } else if (
            attributeType === ATTRIBUTE_TYPES.INTEGER ||
            timePart === TIME_PART_TYPES.DAY_OF_WEEK ||
            timePart === TIME_PART_TYPES.DAY_OF_YEAR
        ) {
            if (isNaN(parseInt(value))) {
                glob.AppActions.showNotification_Fail(
                    `Fail to convert '${value}' value to integer. Types of source and target Attributes must be the same.`,
                    runTimeWidgetID
                );
                return null;
            }
        }

        return value;
    } else {
        return value.toString();
    }
}

function addNormalizedFilterValue(
    value: any,
    attributeType: string,
    timePart: string,
    runTimeWidgetID: number,
    glob: IGlob,
    result: string[]
) {
    if (value != null && value !== '') {
        if (value.value != null) {
            const newValue = normalizeFilterValue(value.value, attributeType, timePart, runTimeWidgetID, glob);

            if (newValue != null) {
                result.push(value);
            }
        } else {
            const newValue = normalizeFilterValue(value, attributeType, timePart, runTimeWidgetID, glob);

            if (newValue != null) {
                result.push(newValue);
            }
        }
    }
}

export function createFilters(
    attributes: IDDAttribute[],
    dataSourceID: number,
    resetFilters: boolean,
    globalState: GlobalState,
    scopeOptions: IScopeOptions,
    entityModelsStore: EntityModelsStore,
    getAppliedData: boolean,
    theme: IValuesCollection,
    cultureValues: Globalization.ICultureValues,
    runTimeWidgetID: number,
    glob: IGlob
): {
    attributeFilters: IGlobalAttributeFilterCollection;
    periodFilter: IPeriodFilter;
} {
    const newAttributeFilterCollection = getAppliedData
        ? $.extend(true, {}, globalState.getAttributeFilterCollection())
        : globalState.getNotAppliedAttributeFilterCollection();

    let resultAttributeFilterCollection: IGlobalAttributeFilterCollection;

    for (const attribute of attributes) {
        const { attributeObject } = attribute;

        let attributeValue = attribute.value;

        if (
            (attributeObject.attribute == null || attributeObject.attribute === '') &&
            attributeObject.expression == null
        ) {
            continue;
        }

        if (attributeValue == null || attributeValue.length === 0) {
            attributeValue = null;
        }

        const clickToFilterAttribute = attributeObject.clickToFilterAttribute;

        let attributeName: string;
        let valueType: QueryFilterValueType;
        let dataQueryAttribute: string | IAttributeName;

        if (clickToFilterAttribute == null) {
            if (attributeObject.attribute == null) {
                // clickToFilterAttribute must be specified for Expressions to be used here.
                continue;
            }

            dataQueryAttribute = attributeName = attributeObject.attribute;
            valueType = 'constant';
        } else {
            attributeName = clickToFilterAttribute.attribute;

            if (attributeName === CLICK_TO_FILTER_NOT_USED_VALUE) {
                continue;
            }

            valueType = clickToFilterAttribute.valueType;

            if (valueType === 'variable') {
                dataQueryAttribute = attributeObject;
            } else {
                dataQueryAttribute = attributeName;
            }
        }

        const attributeType = entityModelsStore.getAttributeType(dataQueryAttribute, dataSourceID);
        const values: string[] = [];
        const timeZone = getTimeZoneFromTheme(theme);

        let intervalParameters: IIntervalParameters;
        let timePart: string;

        if (isTypeOfDate(attributeType)) {
            const { intervals } = attributeObject;

            if (intervals != null && intervals.timePart != null) {
                const intervalsWithFiscalMonth = globalState.applyDinamicFiscalMonth(intervals, scopeOptions);

                intervalParameters = convertIntervalValueToAttrQueryParam(
                    intervalsWithFiscalMonth,
                    timeZone,
                    attributeType,
                    theme,
                    cultureValues
                );
                timePart = intervalParameters.timePart;

                if (intervalParameters.timePeriodStartMonth == null && isFiscalTimePart(timePart)) {
                    intervalParameters.timePeriodStartMonth = intervals.timePeriodStartMonth;
                }
            }
        }

        if (attributeValue != null) {
            if (Array.isArray(attributeValue)) {
                attributeValue.forEach((value) => {
                    addNormalizedFilterValue(value, attributeType, timePart, runTimeWidgetID, glob, values);
                });
            } else {
                addNormalizedFilterValue(attributeValue, attributeType, timePart, runTimeWidgetID, glob, values);
            }
        }

        let selectionType = attributeObject.selectionType || 'multiply';
        let controlType = attributeObject.controlType;

        if (controlType == null) {
            controlType = convertAttributeTypeToControlType(attributeType);
        }

        if (attributeType === ATTRIBUTE_TYPES.BOOLEAN) {
            selectionType = 'single';
        } else if (isTypeOfDate(attributeType)) {
            if (intervalParameters != null) {
                const firstWeekDay = intervalParameters.firstWeekDay;

                if (timePart === TIME_PART_TYPES.DAY_OF_WEEK && firstWeekDay != null) {
                    for (let i = 0; i < values.length; i++) {
                        let day = parseInt(values[i], 10);

                        day += firstWeekDay - 1;

                        if (day > 6) {
                            day -= 7;
                        }

                        values[i] = day.toString();
                    }
                }
            } else if (valueType !== 'variable') {
                // It has been decided that Click to Filter should not create filters for attributes of Date type.
                // However, Variables can be created to be used in other places.
                continue;
            }
        }

        const keySelect = isKeySelect(controlType, attribute.options);
        const normalizedValues = getNormalizedDisplayValues(values, keySelect).values;

        if (normalizedValues == null) {
            continue;
        }

        let resultAttributeFilters: IGlobalAttributeFilters;

        if (resultAttributeFilterCollection == null) {
            resultAttributeFilterCollection = {};
        } else {
            resultAttributeFilters = resultAttributeFilterCollection[dataSourceID.toString()];
        }

        if (resultAttributeFilters == null) {
            resultAttributeFilterCollection[dataSourceID] = resultAttributeFilters = {};
        }

        let foundAttributeFilters = globalState.getAttributeFilters(
            dataSourceID,
            scopeOptions,
            newAttributeFilterCollection
        );

        if (foundAttributeFilters == null) {
            newAttributeFilterCollection[dataSourceID.toString()] = foundAttributeFilters = {};
        }

        const existingFilters = getExistingAttributeFilters(
            attributeName,
            foundAttributeFilters,
            valueType,
            timePart,
            resetFilters
        );

        if (existingFilters == null) {
            let newFilter: IGlobalAttributeFilter = {
                id: attributeName,
                attribute: attributeName,
                selectionType: selectionType,
                clickToFilterAttribute: true,
                values: normalizedValues
            };

            if (scopeOptions != null) {
                newFilter.scopeOptions = { ...scopeOptions };
            }

            if (clickToFilterAttribute != null) {
                newFilter = _.extend(newFilter, clickToFilterAttribute);
            }

            if (intervalParameters != null) {
                newFilter.intervals = $.extend(true, {}, intervalParameters);
                newFilter.controlType = timePart;
            } else if (controlType != null) {
                newFilter.controlType = controlType;
            }

            const id = createAttributeID(newFilter, dataSourceID, foundAttributeFilters);

            newFilter = $.extend(true, {}, newFilter);
            newFilter.id = id;

            foundAttributeFilters[id] = newFilter;
            resultAttributeFilters[newFilter.id] = newFilter;
        } else {
            existingFilters.forEach((filter) => {
                if (filter.values == null || filter.controlType === 'text' || filter.selectionType === 'single') {
                    filter.values = normalizedValues;
                } else {
                    const filterValues = filter.values;

                    normalizedValues.forEach((value) => {
                        if (
                            filterValues.find((filterValue) => {
                                return filterValue.value === value.value;
                            }) == null
                        ) {
                            filterValues.push(value);
                        }
                    });
                }

                const newFilter = { ...filter };

                newFilter.widgetID = null;
                resultAttributeFilters[newFilter.id] = newFilter;
            });
        }
    }

    const periodFilter = getAppliedData
        ? $.extend(true, {}, globalState.getPeriodFilter(scopeOptions))
        : globalState.getNotAppliedPeriodFilter(scopeOptions);

    return {
        attributeFilters: resultAttributeFilterCollection,
        periodFilter: periodFilter
    };
}

function getExistingAttributeFilters(
    name: string,
    filters: IGlobalAttributeFilters,
    valueType: QueryFilterValueType,
    timePart: string,
    resetFilters: boolean
): IGlobalAttributeFilter[] {
    if (filters == null) {
        return null;
    }

    if (valueType == null) {
        valueType = 'constant';
    }

    const result = _.filter(filters, (filter) => {
        let found =
            (!resetFilters || !filter.clickToFilterAttribute) &&
            filter.attribute === name &&
            (filter.valueType || 'constant') === valueType &&
            (timePart == null || (filter.intervals != null && filter.intervals.timePart === timePart));

        if (found && filter.options != null && valueType !== 'variable') {
            const options = filter.options;

            found = !relationDoesNotAllowToHaveValue(options.relation);
        }

        return found;
    });

    if (result != null && result.length > 0) {
        return result;
    }

    return null;
}
